// client/src/pages/secure/PropertyDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicListingPanel from '../../components/secure/PublicListingPanel';
import { useApiClient } from '../../hooks/useApiClient';
import DocumentManager from '../../components/secure/DocumentManager';
import Showings from '../../components/secure/Showings';
import { StagedImage, isStaged } from '../../components/shared/VirtualStagingBadge';
import DataList from '../../components/shared/DataList';

const PROPERTY_DOCUMENT_TYPES = ['Listing Agreement', 'Seller Disclosure', 'Survey', 'HOA Documents', 'Other'];

// --- Photo Gallery Component ---
const PropertyGallery = ({ propertyId }) => {
    const [media, setMedia] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const apiClient = useApiClient();

    const fetchMedia = useCallback(() => {
        setIsLoading(true);
        apiClient.get(`/api/properties/${propertyId}/media`)
            .then(setMedia)
            .catch(err => setError(err.message || 'Failed to load photos.'))
            .finally(() => setIsLoading(false));
    }, [propertyId, apiClient]);

    useEffect(() => { fetchMedia(); }, [fetchMedia]);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        setError('');
        const formData = new FormData();
        formData.append('photo', file);
        try {
            await apiClient.post(`/api/properties/${propertyId}/media`, formData, true);
            fetchMedia();
        } catch (err) {
            setError(err.message || 'Failed to upload photo.');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleDelete = async (mediaId) => {
        if (!window.confirm('Delete this photo?')) return;
        try {
            await apiClient.delete(`/api/properties/${propertyId}/media/${mediaId}`);
            setMedia(prev => prev.filter(m => m.media_id !== mediaId));
        } catch (err) {
            alert(err.message || 'Failed to delete photo.');
        }
    };

    return (
        <div className="bg-white p-4 sm:p-8 rounded-lg shadow mb-8">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-slate-800">Photos</h2>
                <label className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 cursor-pointer">
                    {isUploading ? 'Uploading...' : 'Add Photo'}
                    <input type="file" accept="image/*" onChange={handleUpload} disabled={isUploading} className="hidden" />
                </label>
            </div>
            {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
            {isLoading && <p className="text-slate-500">Loading photos...</p>}
            {!isLoading && media.length === 0 && <p className="text-slate-500">No photos uploaded yet.</p>}
            {!isLoading && media.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {media.map(m => (
                        <div key={m.media_id} className="relative group">
                            <StagedImage staged={isStaged(m)} src={m.storage_path} alt={m.caption || 'Property'} className="w-full h-32 object-cover rounded-md border" />
                            {isStaged(m) && <p className="text-xs text-slate-500 mt-1">{m.caption}{m.original_media_id ? ` · original: photo #${m.original_media_id}` : ''}</p>}
                            <button
                                onClick={() => handleDelete(m.media_id)}
                                className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                Delete
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- NEW CODE START ---

// --- Helper Component for Close Icon ---
const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

// --- Edit Property Modal Component ---
const EditPropertyModal = ({ property, isOpen, onSave, onCancel }) => {
    const [formData, setFormData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const apiClient = useApiClient();

    useEffect(() => {
        if (property) {
            setFormData({ ...property });
        }
    }, [property]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const parsedValue = (name === 'listing_price' || name === 'bedrooms' || name === 'bathrooms' || name === 'square_footage' || name === 'year_built')
            ? (value === '' ? null : parseFloat(value))
            : value;
        setFormData(prevData => ({ ...prevData, [name]: parsedValue }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError('');
        try {
            const updatedProperty = await apiClient.put(`/api/properties/${property.property_id}`, formData);
            onSave(updatedProperty);
        } catch (err) {
            setError(err.message || 'Failed to save changes.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen || !formData) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto w-full max-w-2xl">
                <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-slate-800">Edit Property</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-800"><XIcon /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                    {/* Address Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="street_address" className="block text-sm font-medium text-slate-700">Street Address</label>
                            <input type="text" name="street_address" value={formData.street_address} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="city" className="block text-sm font-medium text-slate-700">City</label>
                            <input type="text" name="city" value={formData.city} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="state" className="block text-sm font-medium text-slate-700">State</label>
                            <input type="text" name="state" value={formData.state} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="zip_code" className="block text-sm font-medium text-slate-700">Zip Code</label>
                            <input type="text" name="zip_code" value={formData.zip_code} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                    {/* Details Fields */}
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="listing_price" className="block text-sm font-medium text-slate-700">Listing Price</label>
                            <input type="number" name="listing_price" value={formData.listing_price || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="property_type" className="block text-sm font-medium text-slate-700">Property Type</label>
                            <select name="property_type" value={formData.property_type} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                <option>Single Family</option>
                                <option>Condo</option>
                                <option>Townhouse</option>
                                <option>Multi-Family</option>
                                <option>Land</option>
                            </select>
                        </div>
                    </div>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="bedrooms" className="block text-sm font-medium text-slate-700">Bedrooms</label>
                            <input type="number" name="bedrooms" value={formData.bedrooms || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="bathrooms" className="block text-sm font-medium text-slate-700">Bathrooms</label>
                            <input type="number" step="0.5" name="bathrooms" value={formData.bathrooms || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div>
                            <label htmlFor="square_footage" className="block text-sm font-medium text-slate-700">Square Footage</label>
                            <input type="number" name="square_footage" value={formData.square_footage || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="year_built" className="block text-sm font-medium text-slate-700">Year Built</label>
                            <input type="number" name="year_built" value={formData.year_built || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-slate-700">Status</label>
                        <select name="status" value={formData.status} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                            <option value="For Sale">For Sale</option>
                            <option value="Pending">Pending</option>
                            <option value="Sold">Sold</option>
                            <option value="Withdrawn">Withdrawn</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-slate-700">Description</label>
                        <textarea name="description" value={formData.description || ''} onChange={handleChange} rows="4" className="mt-1 block w-full p-2 border border-gray-300 rounded-md"></textarea>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
                        <div>
                            <label htmlFor="lockbox_code" className="block text-sm font-medium text-slate-700">Lockbox Code</label>
                            <input type="text" name="lockbox_code" value={formData.lockbox_code || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="listing_expiration_date" className="block text-sm font-medium text-slate-700">Listing Expiration Date</label>
                            <input type="date" name="listing_expiration_date" value={formData.listing_expiration_date ? String(formData.listing_expiration_date).split('T')[0] : ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="showing_instructions" className="block text-sm font-medium text-slate-700">Showing Instructions</label>
                        <textarea name="showing_instructions" value={formData.showing_instructions || ''} onChange={handleChange} rows="3" placeholder="Access notes, appointment requirements, etc." className="mt-1 block w-full p-2 border border-gray-300 rounded-md"></textarea>
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
// --- NEW CODE END ---

const PropertyDetailPage = () => {
    const { id } = useParams();
    const [property, setProperty] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const apiClient = useApiClient();
    // --- NEW CODE START ---
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // --- NEW CODE END ---

    const fetchPropertyData = useCallback(async () => {
        try {
            const [propertyData, transactionsData] = await Promise.all([
                apiClient.get(`/api/properties/${id}`),
                apiClient.get(`/api/properties/${id}/transactions`)
            ]);
            setProperty(propertyData);
            setTransactions(transactionsData);
        } catch (err) {
            setError(err.message || 'Failed to fetch property data.');
        } finally {
            setIsLoading(false);
        }
    }, [id, apiClient]);

    useEffect(() => {
        fetchPropertyData();
    }, [fetchPropertyData]);

    // --- NEW CODE START ---
    const handlePropertyUpdated = (updatedProperty) => {
        setProperty(updatedProperty);
        setIsEditModalOpen(false);
    };
    // --- NEW CODE END ---

    const formatCurrency = (amount) => {
        if (amount === null || isNaN(amount)) return 'N/A';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
    };

    if (isLoading) return <div className="p-8">Loading property details...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;
    if (!property) return <div className="p-8">Property not found.</div>;

    return (
        <>
            {/* --- NEW CODE START --- */}
            <EditPropertyModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSave={handlePropertyUpdated}
                property={property}
            />
            {/* --- NEW CODE END --- */}
            <div>
                <div className="mb-6">
                    <Link to="/dashboard/properties" className="text-indigo-600 hover:text-indigo-900">&larr; Back to Property List</Link>
                </div>

                {/* Property Details Card */}
                <div className="bg-white p-4 sm:p-8 rounded-lg shadow mb-8">
                    {/* MODIFIED: Added a flex container for title and menu */}
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-800">{property.street_address}</h1>
                            <p className="text-lg text-slate-600">{property.city}, {property.state} {property.zip_code}</p>
                        </div>
                        {/* --- NEW CODE START --- */}
                        <div className="relative">
                            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700">
                                Manage
                            </button>
                            {isMenuOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                                    <button
                                        onClick={() => {
                                            setIsEditModalOpen(true);
                                            setIsMenuOpen(false);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        Edit Property
                                    </button>
                                    <Link to={`/dashboard/cma?new=1&property_id=${Number(id)}`}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                        Prepare CMA
                                    </Link>
                                </div>
                            )}
                        </div>
                        {/* --- NEW CODE END --- */}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t pt-4">
                        <div><p className="text-sm font-medium text-slate-500">Type</p><p className="text-lg text-slate-900">{property.property_type}</p></div>
                        <div><p className="text-sm font-medium text-slate-500">Status</p><p className="text-lg text-slate-900">{property.status}</p></div>
                        <div><p className="text-sm font-medium text-slate-500">Price</p><p className="text-lg text-slate-900">{formatCurrency(property.listing_price)}</p></div>
                    </div>
                    <div className="mt-4 border-t pt-4">
                         <p className="text-sm font-medium text-slate-500">Description</p>
                         <p className="text-slate-700 mt-1">{property.description || 'No description provided.'}</p>
                    </div>
                    {(property.showing_instructions || property.lockbox_code || property.listing_expiration_date) && (
                        <div className="mt-4 border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {property.lockbox_code && <div><p className="text-sm font-medium text-slate-500">Lockbox Code</p><p className="text-lg text-slate-900">{property.lockbox_code}</p></div>}
                            {property.listing_expiration_date && <div><p className="text-sm font-medium text-slate-500">Listing Expires</p><p className="text-lg text-slate-900">{new Date(property.listing_expiration_date).toLocaleDateString()}</p></div>}
                            {property.showing_instructions && <div className="md:col-span-2"><p className="text-sm font-medium text-slate-500">Showing Instructions</p><p className="text-slate-700 mt-1 whitespace-pre-wrap">{property.showing_instructions}</p></div>}
                        </div>
                    )}
                </div>

                <PropertyGallery propertyId={id} />

                <PublicListingPanel key={property.property_id} property={property} onSaved={setProperty} />

                {/* Transaction History Card */}
                <div className="bg-white p-4 sm:p-8 rounded-lg shadow">
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Transaction History</h2>
                    <div className="overflow-x-auto">
                        <div className="p-4"><DataList rows={transactions} rowKey="transaction_id" csvName="property-transactions" defaultSort={{ key: 'actual_close_date', dir: 'desc' }} empty="No transactions found for this property."
                            filters={[{ key: 'transaction_type', label: 'Type' }, { key: 'status', label: 'Status' }]}
                            columns={[
                                { key: 'transaction_id', label: 'Transaction ID', type: 'number', render: (t) => <span className="font-medium text-slate-900">{t.transaction_id}</span> },
                                { key: 'transaction_type', label: 'Type' },
                                { key: 'status', label: 'Status' },
                                { key: 'actual_close_date', label: 'Close Date', type: 'date', render: (t) => (t.actual_close_date ? new Date(t.actual_close_date).toLocaleDateString() : 'N/A') },
                            ]} /></div>
                    </div>
                </div>

                <div className="mt-8">
                    <DocumentManager relatedId={id} relatedType="property" documentTypes={PROPERTY_DOCUMENT_TYPES} />
                </div>

                <Showings propertyId={id} />
            </div>
        </>
    );
};

export default PropertyDetailPage;
